import {
  ActionChip,
  Cell,
  colors,
  LayoutBand,
  Row,
  Separator,
  Skeleton,
  useContainerWidth,
  WhiteTheme,
} from '@fs/zion-ui'
import { MenuSearch, ControlSettingsAdjust } from '@fs/zion-icon'
import { Person } from '@fs/zion-person'
import { css } from '@emotion/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useQualityIndicatorFlag from '../../../data-service/useQualityIndicatorFlag'
import PersonQualityScoreButtonSkeleton from '../../../common/PersonQualityScoreButtonSkeleton'
import BannerActionsSkeleton from './BannerActionsSkeleton'
import { BannerExperimentChipsSkeleton } from './BannerExperimentChips'
import LastEditedIndicatorSkeleton from './LastEditedIndicatorSkeleton'
import { SOURCE_CENTRIC_GUTTERS } from '../../constants'

// Mirrors the actions row in SourceCentricBanner — see the comment there for why space-between.
const actionsRowCss = css`
  justify-content: space-between;
`

const actionsAlignCss = css`
  margin-inline-start: var(--start-padding);
`

export default function SourceCentricBannerSkeleton(): JSX.Element {
  const [t] = useTranslation()
  const atSize = useContainerWidth()
  const avatarSize = atSize({ default: 'md', lg: 'lg' })
  const isDesktop = atSize({ default: false, lg: true })
  const hideText = atSize({ default: true, md: false })
  const { data: qualityIndicatorFlag } = useQualityIndicatorFlag()
  const showQualityScore = !!qualityIndicatorFlag?.isOn

  return (
    <div style={{ overflow: 'hidden' }}>
      <LayoutBand maxWidth="lg" color={colors.gray90} marginY={SOURCE_CENTRIC_GUTTERS} marginX={SOURCE_CENTRIC_GUTTERS}>
        <WhiteTheme>
          <Row alignX="end" alignY="top" gutters="xs" guttersY="nano" wrap>
            <Cell growX>
              <Row alignY="middle">
                <Skeleton.Avatar size={avatarSize} />
                <Person loading hideAvatar componentSize="xl" lifespanSize="long" />
              </Row>
            </Cell>
            {isDesktop && (
              <Cell noShrinkX>
                <Row alignX="end" alignY="top" gutters="xxs" guttersY="nano" wrap>
                  {/* eslint-disable-next-line test-selectors/button -- NA */}
                  {showQualityScore && <PersonQualityScoreButtonSkeleton variant="chip" />}
                  <LastEditedIndicatorSkeleton />
                  <Skeleton.Chip width={155} />
                  <Skeleton.Chip width={90} />
                </Row>
              </Cell>
            )}
          </Row>
          <Separator size="xs" />
          <Row css={actionsRowCss} alignY="middle" wrap guttersY="xs">
            <div
              css={actionsAlignCss}
              style={{
                '--start-padding': atSize({
                  default: 'calc(64px + var(--spacer-sm) - 8px)',
                  lg: 'calc(88px + var(--spacer-sm) - 8px)',
                }),
              }}
            >
              <BannerActionsSkeleton hideText={hideText} />
            </div>
            <BannerExperimentChipsSkeleton />
          </Row>
          {!isDesktop && (
            <>
              <Separator size="xs" />
              <Row wrap alignY="middle" gutters="xxs" guttersY="nano">
                {/* eslint-disable-next-line test-selectors/button -- NA */}
                {showQualityScore && <PersonQualityScoreButtonSkeleton short variant="chip" />}
                {/* last-edited indicator placeholder — mirrors the desktop LastEditedIndicatorSkeleton */}
                <Skeleton.Chip width={200} />
                <ActionChip
                  Icon={MenuSearch}
                  emphasis="high"
                  disabled
                  data-testid="banner-search-records-menu"
                  label={t('tree-person.search-records.label')}
                />
                <ActionChip
                  Icon={ControlSettingsAdjust}
                  disabled
                  data-testid="banner-tools-menu"
                  label={t('tree-person.tools.label')}
                />
              </Row>
            </>
          )}
        </WhiteTheme>
      </LayoutBand>
    </div>
  )
}
